import { ColumnMappingData } from '../../pages/ExcelUploadPage';

// Mirrors the backend's leg-creation rule (xmlCalcController.mapRowToShipment):
// a leg exists for a row iff it has origin AND destination data, where either
// side counts a country, free address, or city — a postal code alone does not.
// Destination columns may double as the next leg's origin via ||-multi-select
// ("chained" mappings), so presence must be evaluated per row, not per mapping.

const ORIGIN_PRESENCE_FIELDS = ['origin_country', 'origin_free_address', 'origin_city'];
const DEST_PRESENCE_FIELDS = ['destination_country', 'destination_free_address', 'destination_city'];

export const getLegNumber = (fieldId: string): number | null => {
  const match = /_leg(\d+)$/.exec(fieldId);
  return match ? Number.parseInt(match[1], 10) : null;
};

const findMappingKey = (mappings: ColumnMappingData, fieldId: string): string | undefined =>
  Object.keys(mappings).find((key) => {
    const value = mappings[key];
    return value === fieldId || (value.includes('||') && value.split('||').includes(fieldId));
  });

/** Resolve the value a field would receive for this row (columns and MANUAL constants). */
export const getMappedCellValue = (
  row: (string | number)[],
  mappings: ColumnMappingData,
  fieldId: string
): string => {
  const colKey = findMappingKey(mappings, fieldId);
  if (!colKey) return '';
  if (colKey.startsWith('MANUAL:')) return colKey.replace('MANUAL:', '').trim();
  const colIndex = Number.parseInt(colKey, 10);
  if (Number.isNaN(colIndex) || colIndex < 0 || colIndex >= row.length) return '';
  return String(row[colIndex] ?? '').trim();
};

/** Column index a field is mapped to, or null (MANUAL constants have no column). */
export const findMappedColumnIndex = (
  mappings: ColumnMappingData,
  fieldId: string
): number | null => {
  const colKey = findMappingKey(mappings, fieldId);
  if (!colKey || colKey.startsWith('MANUAL:')) return null;
  const colIndex = Number.parseInt(colKey, 10);
  return Number.isNaN(colIndex) ? null : colIndex;
};

export const isLegPresent = (
  row: (string | number)[],
  mappings: ColumnMappingData,
  legNumber: number,
  manualFieldIds?: ReadonlySet<string>
): boolean => {
  const hasAny = (fields: string[]) =>
    fields.some((field) => {
      const fieldId = `${field}_leg${legNumber}`;
      if (manualFieldIds?.has(fieldId)) return true;
      return getMappedCellValue(row, mappings, fieldId) !== '';
    });
  return hasAny(ORIGIN_PRESENCE_FIELDS) && hasAny(DEST_PRESENCE_FIELDS);
};

/** Leg numbers referenced anywhere in the mappings, ascending. */
export const mappedLegNumbers = (mappings: ColumnMappingData): number[] => {
  const legs = new Set<number>();
  for (const value of Object.values(mappings)) {
    for (const fieldId of value.split('||')) {
      const legNumber = getLegNumber(fieldId);
      if (legNumber) legs.add(legNumber);
    }
  }
  return Array.from(legs).sort((a, b) => a - b);
};
